import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/Loading';
import { DELIVERY_STATUS_LABEL, REQUEST_STATUS_LABEL, pastorApi, errorMessage } from '../pastorApi';
import type { DeliveryStatus, InboxDelivery, InboxPromotion, MyRequest } from '../pastorApi';
import { usePastorMe } from '../usePastorMe';

const statusClass = (s: DeliveryStatus) => (s === 'DELIVERED' ? 'accepted' : s === 'CONSENT_PENDING' || s === 'INTERESTED' ? 'pending' : s === 'DECLINED' ? 'declined' : 'muted');

// 내 청빙 소식(사역자) / 내 청빙 현황(교회 — 로그인해서 낸 의뢰)
export default function MyPastorCall() {
  const navigate = useNavigate();
  const { isLogin, loading, isChurch, me } = usePastorMe();

  if (!isLogin) {
    return (
      <div className="pastor-call inner pc-page">
        <div className="pc-gate">
          <p className="pc-gate__title">로그인 후 확인할 수 있습니다</p>
          <p>목사님은 교회 추천과 청빙 소식을, 교회는 의뢰 진행 현황과 전달받은 이력서를 볼 수 있습니다.</p>
          <button type="button" className="mm-btn mm-btn--primary pc-btn-lg" onClick={() => navigate('/login')}>로그인하기</button>
        </div>
      </div>
    );
  }
  if (loading) return <div className="pastor-call inner pc-page"><Loading /></div>;
  if (isChurch || !me?.pastorProfile) return <ChurchStatus showPastorCta={!isChurch} />;
  return <PastorInbox />;
}

function PastorInbox() {
  const navigate = useNavigate();
  const [data, setData] = useState<{ deliveries: InboxDelivery[]; promotions: InboxPromotion[] } | null>(null);
  const [declineId, setDeclineId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    pastorApi.inbox().then(setData).catch(() => setData({ deliveries: [], promotions: [] }));
  }, []);
  useEffect(load, [load]);

  const respond = async (id: number, accept: boolean) => {
    if (accept && !window.confirm('동의하시면 이 교회 한 곳에 목사님의 이력서와 연락처가 바로 전달됩니다. 동의할까요?')) return;
    setBusy(true);
    try {
      await pastorApi.respond(id, accept, accept ? undefined : note);
      setDeclineId(null);
      setNote('');
      load();
    } catch (e) {
      alert(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="pastor-call inner pc-page"><Loading /></div>;
  const pending = data.deliveries.filter((d) => d.status === 'CONSENT_PENDING');
  const others = data.deliveries.filter((d) => d.status !== 'CONSENT_PENDING');

  return (
    <div className="pastor-call inner pc-page">
      <div className="pc-page__head">
        <h2>내 청빙 소식</h2>
        <div className="pc-page__tools">
          <button type="button" className="mm-btn mm-btn--outline pc-btn-sm" onClick={() => navigate('/pastor/profile')}>내 이력서</button>
          <button type="button" className="mm-btn mm-btn--outline pc-btn-sm" onClick={() => navigate('/pastor/jobs')}>청빙 소식 전체</button>
        </div>
      </div>

      <section className="pc-sect--flat">
        <div className="mm-sect"><h3>사역자모아가 추천하는 교회</h3><span className="sub">동의하시면 이력서를 전달합니다</span></div>
        {pending.length === 0 ? (
          <div className="pc-empty"><p>지금 응답을 기다리는 추천이 없습니다.</p><p>맞는 교회가 있으면 앱 알림과 이메일로 먼저 여쭙겠습니다.</p></div>
        ) : (
          <ul className="pc-plist">
            {pending.map((d) => (
              <li key={d.id} className="pc-pitem is-pending">
                <ChurchHead d={d} />
                {d.adminMessage && <p className="pc-pre pc-pitem__msg">사역자모아: {d.adminMessage}</p>}
                <ChurchRows d={d} />
                {declineId === d.id ? (
                  <div className="pc-decline">
                    <textarea className="mm-field" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="사역자모아에 남길 말씀 (선택) — 교회에는 전달되지 않습니다" />
                    <div className="pc-pitem__btns">
                      <button type="button" className="mm-btn mm-btn--outline pc-btn-md" onClick={() => setDeclineId(null)}>돌아가기</button>
                      <button type="button" className="mm-btn mm-btn--primary pc-btn-md" disabled={busy} onClick={() => respond(d.id, false)}>이번에는 사양하기</button>
                    </div>
                  </div>
                ) : (
                  <div className="pc-pitem__btns">
                    <button type="button" className="mm-btn mm-btn--outline pc-btn-md" onClick={() => setDeclineId(d.id)}>사양하기</button>
                    <button type="button" className="mm-btn mm-btn--primary pc-btn-md" disabled={busy} onClick={() => respond(d.id, true)}>동의하고 이력서 전달</button>
                  </div>
                )}
                <p className="pc-hint">응답 기한 {d.expiresAt?.slice(0, 10)} · 사양하셔도 교회는 목사님이 누구신지 알 수 없습니다.</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="pc-sect--flat">
        <div className="mm-sect"><h3>받은 청빙 소식</h3></div>
        {data.promotions.length === 0 ? (
          <div className="pc-empty"><p>아직 받은 청빙 소식이 없습니다.</p></div>
        ) : (
          <ul className="pc-plist">
            {data.promotions.map((p) => (
              <li
                key={p.id}
                className="pc-pitem pc-pitem--link"
                onClick={() => {
                  if (p.sourceType === 'RECRUIT' && p.link) window.location.href = p.link;
                  else navigate('/pastor/jobs');
                }}
              >
                <div className="pc-pitem__head">
                  <div>
                    <p className="pc-pitem__title">{p.title}</p>
                    <p className="pc-pitem__meta">{[p.church, p.region, `${p.sentAt.slice(0, 10)} 받음`].filter(Boolean).join(' · ')}</p>
                  </div>
                  <span className="pc-pitem__go">{p.interested ? '전달 요청함' : '자세히 →'}</span>
                </div>
                {p.message && <p className="pc-hint">{p.message}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {others.length > 0 && (
        <section className="pc-sect--flat">
          <div className="mm-sect"><h3>지난 추천·전달 내역</h3></div>
          <ul className="pc-plist">
            {others.map((d) => (
              <li key={d.id} className="pc-pitem">
                <ChurchHead d={d} />
                <p className="pc-pitem__meta">
                  {d.status === 'DELIVERED'
                    ? `${d.deliveredAt?.slice(0, 10)} 이력서 전달 · ${d.churchViewed ? '교회가 열람했습니다' : '아직 열람 전'}`
                    : d.status === 'INTERESTED' ? '사역자모아가 확인 중입니다' : DELIVERY_STATUS_LABEL[d.status]}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ChurchHead({ d }: { d: InboxDelivery }) {
  return (
    <div className="pc-pitem__head">
      <div>
        <p className="pc-pitem__title">{d.church.churchName}</p>
        <p className="pc-pitem__meta">{[d.church.denomination, d.church.region, d.church.regionType, d.church.attendance && `출석 ${d.church.attendance}`].filter(Boolean).join(' · ')}</p>
      </div>
      <span className={`pc-status pc-status--${statusClass(d.status)}`}>{DELIVERY_STATUS_LABEL[d.status]}</span>
    </div>
  );
}

function ChurchRows({ d }: { d: InboxDelivery }) {
  const c = d.church;
  return (
    <dl className="pc-pitem__rows">
      {c.intro && <div><dt>교회 소개</dt><dd className="pc-pre">{c.intro}</dd></div>}
      {c.desiredNote && <div><dt>원하는 목사님</dt><dd className="pc-pre">{c.desiredNote}</dd></div>}
      {c.offerConditions.length > 0 && <div><dt>제공 조건</dt><dd>{c.offerConditions.join(', ')}</dd></div>}
      {c.salaryNote && <div><dt>처우</dt><dd>{c.salaryNote}</dd></div>}
      {c.recruitPath && <div><dt>공고</dt><dd><a href={c.recruitPath}>구인정보 공고 보기</a></dd></div>}
    </dl>
  );
}

function ChurchStatus({ showPastorCta }: { showPastorCta: boolean }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<MyRequest[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    pastorApi.myRequests().then(setItems).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const act = async (message: string, run: () => Promise<unknown>) => {
    if (!window.confirm(message)) return;
    setBusy(true);
    try {
      await run();
      load();
    } catch (e) {
      alert(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const finish = (r: MyRequest, status: 'DONE' | 'CLOSED') =>
    act(
      status === 'DONE'
        ? `'${r.churchName}' 청빙을 완료로 표시할까요? 응답을 기다리는 추천은 모두 종료됩니다.`
        : `'${r.churchName}' 청빙 의뢰를 종료할까요? 응답을 기다리는 추천은 모두 종료됩니다.`,
      () => pastorApi.finishRequest(r.id, status),
    );
  const remove = (r: MyRequest) =>
    act(
      `'${r.churchName}' 청빙 의뢰서를 삭제할까요? 진행 중인 추천이 취소되고, 전달받은 이력서도 더는 열람할 수 없습니다. 되돌릴 수 없습니다.`,
      () => pastorApi.deleteRequest(r.id),
    );

  if (!items) return <div className="pastor-call inner pc-page"><Loading /></div>;

  return (
    <div className="pastor-call inner pc-page">
      <div className="pc-page__head">
        <h2>내 청빙 현황</h2>
        <div className="pc-page__tools">
          <button type="button" className="mm-btn mm-btn--primary pc-btn-sm" onClick={() => navigate('/pastor/request')}>새 청빙 의뢰</button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="pc-empty">
          <p>이 계정으로 낸 청빙 의뢰가 없습니다.</p>
          <p>담임목사님을 찾으시면 청빙 의뢰서를 보내 주세요. 사역자모아가 맞는 목사님을 찾아 이력서를 전달해 드립니다.</p>
          <p>로그인하지 않고 내신 의뢰는 이곳에 보이지 않습니다. 수정·삭제가 필요하시면 오른쪽 아래 [청빙 대행 상담]으로 알려 주세요.</p>
          {showPastorCta && (
            <p>
              담임목회를 희망하시는 목사님이시면{' '}
              <button type="button" className="mm-btn--ghost" onClick={() => navigate('/pastor/profile')}>이력서를 맡겨 주세요 →</button>
            </p>
          )}
        </div>
      ) : (
        <ul className="pc-plist">
          {items.map((r) => (
            <li key={r.id} className="pc-pitem">
              <div className="pc-pitem__head">
                <div>
                  <p className="pc-pitem__title">{r.churchName}</p>
                  <p className="pc-pitem__meta">{r.createdAt?.slice(0, 10)} 의뢰 · {[r.sido, r.sigungu].filter(Boolean).join(' ')}</p>
                </div>
                <span className={`pc-status pc-status--${r.status === 'DONE' ? 'accepted' : r.status === 'CLOSED' ? 'muted' : 'pending'}`}>{REQUEST_STATUS_LABEL[r.status]}</span>
              </div>
              {openId === r.id && <RequestRows r={r} />}
              {r.candidates.length === 0 ? (
                <p className="pc-hint">
                  {['DONE', 'CLOSED'].includes(r.status)
                    ? '마감된 의뢰입니다.'
                    : '사역자모아가 맞는 목사님을 찾고 있습니다. 목사님이 동의하시면 이력서가 이곳과 담당자 이메일로 전달됩니다.'}
                </p>
              ) : (
                <dl className="pc-pitem__rows">
                  {r.candidates.map((c) => (
                    <div key={c.viewPath}>
                      <dt>{c.deliveredAt.slice(0, 10)}</dt>
                      <dd>
                        <b>{c.name} 목사</b> · {c.summary}{' '}
                        <button type="button" className="mm-btn--ghost" onClick={() => navigate(c.viewPath)}>이력서 보기 →</button>
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              <div className="pc-pitem__btns">
                <button type="button" className="mm-btn mm-btn--outline pc-btn-md" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                  {openId === r.id ? '의뢰 내용 접기' : '의뢰 내용 보기'}
                </button>
                <button type="button" className="mm-btn mm-btn--outline pc-btn-md" onClick={() => navigate(`/pastor/request?edit=${r.id}`)}>수정</button>
                {!['DONE', 'CLOSED'].includes(r.status) && (
                  <>
                    <button type="button" className="mm-btn mm-btn--outline pc-btn-md" disabled={busy} onClick={() => finish(r, 'DONE')}>청빙 완료</button>
                    <button type="button" className="mm-btn mm-btn--outline pc-btn-md" disabled={busy} onClick={() => finish(r, 'CLOSED')}>의뢰 종료</button>
                  </>
                )}
                <button type="button" className="mm-btn mm-btn--ghost pc-btn-md" disabled={busy} onClick={() => remove(r)}>삭제</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RequestRows({ r }: { r: MyRequest }) {
  const rows: [string, string][] = [
    ['교단', r.denomination],
    ['주소', r.address],
    ['지역 유형', r.regionType],
    ['출석 규모', r.attendance],
    ['설립 연도', r.foundedYear ? `${r.foundedYear}년` : ''],
    ['교회 소개', r.intro],
    ['제공 조건', r.offerConditions.join(', ')],
    ['사례비·처우', r.salaryNote],
    ['원하는 목사님', r.desiredNote],
    ['홍보 동의', r.allowPromote ? '목사님들께 알려도 됨' : '알리지 않음'],
    ['담당자', [r.managerName, r.managerRole].filter(Boolean).join(' ')],
    ['연락처', [r.managerPhone, r.managerEmail].filter(Boolean).join(' · ')],
  ];
  return (
    <dl className="pc-pitem__rows">
      {rows.filter(([, v]) => v).map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd className="pc-pre">{v}</dd></div>
      ))}
    </dl>
  );
}
